import axios from 'axios';
import { config } from '../config';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { Claim, LossType } from '../store/claimsSlice';

export interface InventoryAttachment {
  id: string;
  type: string;
  original_filename: string;
  token?: string;
  path?: string;
}

export interface InventoryItem {
  id: number;
  tpp_claim_id: number;
  item_description: string;
  damage_description?: string | null;
  date_of_purchase: string | null;
  price: string | number | null;
  attachments?: InventoryAttachment[];
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  };

  // Prioritize access_token over API_KEY
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  } else {
    // Fallback to API_KEY if no token is available
    headers['x-api-key'] = config.API_KEY || '';
  }

  return headers;
};

export const getLossTypes = async (): Promise<LossType[]> => {
  try {
    const response = await axios.get(`${config.API_URL}/loss-types`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching loss types:', error);
    throw error;
  }
};

export const fetchClaims = createAsyncThunk(
  'claims/fetchAll',
  async ({ search, page, pageSize, sortBy, sortOrder, filters }: {
    search?: string;
    page?: number;
    pageSize?: number;
    sortBy?: string;
    sortOrder?: string;
    filters?: Record<string, string | string[]>;
  } = {}) => {
    try {
      const params = new URLSearchParams();
      // Send each word as a separate search param so "John Smith" matches claims with both terms
      if (search && search.trim()) {
        search.trim().split(/\s+/).forEach((term) => {
          if (term) params.append('search', term);
        });
      }
      if (page) params.append('page', page.toString());
      if (pageSize) params.append('pageSize', pageSize.toString());
      if (sortBy) params.append('sortBy', sortBy);
      if (sortOrder) params.append('sortOrder', sortOrder);
      
      // Add filter parameters in the format filter[field]=value
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => {
              if (v) {
                params.append(`filter[${key}]`, v);
              }
            });
          } else if (value) {
            params.append(`filter[${key}]`, value);
          }
        });
      }
      
      const url = `${config.API_URL}/claims${params.toString() ? `?${params.toString()}` : ''}`;
        
      const response = await axios.get(url,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data
    } catch (error) {
      console.error('Error fetching claims:', error);
      throw error;
    }
  }
);

export const fetchClaimById = createAsyncThunk(
  'claims/fetchById',
  async (claimId: string) => {
    try {
      const response = await axios.get(`${config.API_URL}/claims/${claimId}`,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching claim:', error);
      throw error;
    }
  }
);

export interface ValidationErrorPayload {
  message: string;
  errors?: Record<string, string[]>;
}

export const updateClaim = createAsyncThunk<
  Claim,
  Partial<Claim> & { id: string },
  { rejectValue: ValidationErrorPayload }
>(
  'claims/update',
  async (claim, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${config.API_URL}/claims/${claim.id}`,
        claim,
        {
          headers: getAuthHeaders(),
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating claim:', error);
      if (axios.isAxiosError(error) && error.response?.data) {
        const data = error.response.data as { errors?: Record<string, string[]> };
        return rejectWithValue({
          message: 'Failed to update claim',
          errors: data.errors,
        });
      }
      return rejectWithValue({ message: 'Failed to update claim' });
    }
  }
);

export interface FacilitiesPaginatedResponse {
  data: FacilityRecord[];
  total: number;
  current_page: number;
  per_page: number;
  last_page: number;
}

export const getFacilitiesPaginated = async (params: {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  /** Omit for all facilities; true = enabled only; false = disabled only */
  active?: boolean;
} = {}): Promise<FacilitiesPaginatedResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.append('page', String(params.page ?? 1));
  searchParams.append('pageSize', String(params.pageSize ?? 100));
  if (params.sortBy) searchParams.append('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.append('sortOrder', params.sortOrder);
  if (params.search) searchParams.append('search', params.search);
  if (params.active !== undefined) {
    searchParams.append('active', params.active ? '1' : '0');
  }

  const response = await axios.get(`${config.API_URL}/facilities?${searchParams.toString()}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getFacilities = async (sortBy: string = 'location_code', active?: number | boolean): Promise<any[]> => {
  try {
    const params = new URLSearchParams();
    if (active !== undefined) {
      params.append('active', active.toString());
    }
    params.append('sortBy', sortBy);
    
    const url = `${config.API_URL}/facilities${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await axios.get(url,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching facilities:', error);
    throw error;
  }
}; 

export const facilitySearch = async (): Promise<any[]> => {
  try {
    const response = await axios.post(`${config.API_URL}/facilities/search`,
      [],
      {
        headers: getAuthHeaders(),
      }
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error('Error searching facilities:', error);
    throw error;
  }
}; 

export const searchClaims = async (searchParams: {
  facility_id: number | null;
  tpp_loss_type_id: number | null;
  status: string | null;
  created_at_start: string | null;
  created_at_end: string | null;
}) => {
  try {
    const response = await axios.post(`${config.API_URL}/claims/search`,
      searchParams,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error searching claims:', error);
    throw error;
  }
}; 

export const login = async (email: string, password: string): Promise<any> => {
  try {
    const response = await axios.post(`${config.API_URL}/login`,
      { email, password },
      {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          // Login endpoint doesn't require authentication
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error during login:', error);
    throw error;
  }
}; 

export const getClaimAttachment = async (claimId: string, attachmentId: string): Promise<any> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/${claimId}/attachments/${attachmentId}/download`,
      {
        headers: getAuthHeaders(),
        responseType: 'blob'
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching claim attachment:', error);
    throw error;
  }
}; 

export const getClaimAttachmentUrl = async (claimId: string, attachmentId: string): Promise<any> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/${claimId}/attachments/${attachmentId}/download-url`,
      {
        headers: getAuthHeaders(),
      }
    );
    console.log(response);
    return response;
  } catch (error) {
    console.error('Error fetching claim attachment:', error);
    throw error;
  }
}; 

export const deleteClaimAttachment = async (claimId: string, attachmentId: string): Promise<any> => {
  try {
    const response = await axios.delete(`${config.API_URL}/claims/${claimId}/attachments/${attachmentId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting claim attachment:', error);
    throw error;
  }
};

export const getInventoryItems = async (claimId: string): Promise<InventoryItem[]> => {
  try {
    const response = await axios.get(`${config.API_URL}/claims/${claimId}/inventory-items`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory items:', error);
    throw error;
  }
};

/** Streams file via token-authenticated preview endpoint; triggers browser download. */
export const downloadInventoryAttachmentFile = async (
  claimId: string,
  attachmentId: string,
  token: string,
  filename: string
): Promise<void> => {
  const response = await axios.get(
    `${config.API_URL}/claims/${claimId}/inventory-attachments/${attachmentId}/preview?token=${encodeURIComponent(token)}`,
    {
      headers: getAuthHeaders(),
      responseType: 'blob',
    }
  );
  const url = window.URL.createObjectURL(response.data);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

export const uploadClaimAttachment = async (
  claimId: string,
  file: File,
  type: 'document' | 'image' | 'video',
  userId?: string
): Promise<any> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', type);
    if (userId) {
      formData.append('user_id', userId);
    }

    const response = await axios.post(
      `${config.API_URL}/claims/${claimId}/attachments`,
      formData,
      {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error uploading claim attachment:', error);
    throw error;
  }
}; 

export interface StateRecord {
  id: number;
  code: string;
  name: string;
}

export const getStates = async (): Promise<StateRecord[]> => {
  const response = await axios.get(`${config.API_URL}/states`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export interface FacilityRecord {
  id: number;
  location_code: string;
  external_id?: string;
  name?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  phone?: string | null;
  email?: string | null;
  active: boolean;
}

export type CreateFacilityPayload = {
  location_code: string;
  external_id?: string;
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
  email?: string;
  active?: boolean;
};

export const createFacility = async (payload: CreateFacilityPayload): Promise<FacilityRecord> => {
  const response = await axios.post(`${config.API_URL}/facilities`, payload, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateFacility = async (
  facilityId: number,
  payload: Partial<CreateFacilityPayload>
): Promise<FacilityRecord> => {
  const response = await axios.put(`${config.API_URL}/facilities/${facilityId}`, payload, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export interface EmailTemplate {
  id: number;
  tpp_loss_type_id: number;
  email_body: string | null;
  loss_type?: LossType;
  created_at?: string;
  updated_at?: string;
}

export const getEmailTemplate = async (lossTypeId: number): Promise<EmailTemplate> => {
  const response = await axios.get(`${config.API_URL}/email-templates/${lossTypeId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const updateEmailTemplate = async (
  lossTypeId: number,
  emailBody: string
): Promise<EmailTemplate> => {
  const response = await axios.put(
    `${config.API_URL}/email-templates/${lossTypeId}`,
    { email_body: emailBody },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export type ClaimNoteType =
  | 'internal'
  | 'phone_call'
  | 'message_to_tenant'
  | 'message_to_manager'
  | 'message_from_tenant'
  | 'message_from_manager'
  | 'state_change';

export interface ClaimNote {
  id: number;
  claim_id: number;
  note: string;
  note_type: ClaimNoteType | string | null;
  note_type_label: string;
  sender_label: string;
  created_user_id: number | null;
  ack: boolean;
  created_at: string;
  updated_at: string;
  tenant_email_sent?: boolean;
}

export const getClaimNotes = async (claimId: string | number): Promise<ClaimNote[]> => {
  const response = await axios.get(`${config.API_URL}/claims/${claimId}/notes`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const createClaimNote = async (
  claimId: string | number,
  note: string,
  noteType: ClaimNoteType
): Promise<ClaimNote> => {
  const response = await axios.post(
    `${config.API_URL}/claims/${claimId}/notes`,
    { note, note_type: noteType },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

